import { useState } from 'react';
import { Edit, Lock, Briefcase } from 'lucide-react';
import {
  CONTRACT_TYPE_LABELS,
  EMPLOYEE_STATUS_LABELS,
  type Employee,
} from '@/types';
import { formatNumber } from '@/utils/currency';
import { DocumentsEmploye } from './DocumentsEmploye';
import { PaieEmploye } from './PaieEmploye';
import { useAuthStore } from '@/stores/authStore';

interface Props {
  employee: Employee | null;
  onEdit?: (e: Employee) => void;
}

const fmtDateLong = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

const yearsSince = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  const years = (now.getTime() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years < 1) return `${Math.round(years * 12)} mois`;
  return `${years.toFixed(1)} ans`;
};

type RpTab = 'profile' | 'leaves' | 'docs' | 'paie';

const initials = (name: string) =>
  name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

/**
 * Detail panel sticky right CTO-FRONT-002.
 * Référence : design-rh-personnel-v1.html lignes 106-150.
 */
export const EmployeeDetailPanel = ({ employee, onEdit }: Props) => {
  const userRole = useAuthStore((s) => s.user?.role);
  const canSeeSalary = userRole === 'daf' || userRole === 'admin_holding';
  const [tab, setTab] = useState<RpTab>('profile');

  if (!employee) {
    return (
      <div className="ds-detail-panel">
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--tx-3)' }}>
          <Briefcase size={28} style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: 13 }}>Sélectionnez un employé dans la liste pour voir le détail</p>
        </div>
      </div>
    );
  }

  const salaryFcfa = employee.salaryCents ? Number(BigInt(employee.salaryCents)) / 100 : null;

  return (
    <div className="ds-detail-panel">
      {/* Header gradient avec avatar */}
      <header className="ds-dp-header">
        <div className="ds-dp-av">{initials(employee.fullName)}</div>
        <div className="ds-dp-name">{employee.fullName}</div>
        <div className="ds-dp-role">{employee.jobTitle}</div>
        <div className="ds-dp-badges">
          <span className="ds-dp-badge">{CONTRACT_TYPE_LABELS[employee.contractType]}</span>
          <span className="ds-dp-badge">{EMPLOYEE_STATUS_LABELS[employee.status]}</span>
        </div>
      </header>

      <div className="ds-dp-body">
        {/* Tabs Profil / Congés / Docs */}
        <div className="ds-rp-tabs">
          <button className={`ds-rp-tab${tab === 'profile' ? ' on' : ''}`} onClick={() => setTab('profile')}>👤 Profil</button>
          <button className={`ds-rp-tab${tab === 'leaves'  ? ' on' : ''}`} onClick={() => setTab('leaves')}>🏖️ Congés</button>
          <button className={`ds-rp-tab${tab === 'docs'    ? ' on' : ''}`} onClick={() => setTab('docs')}>📄 Docs</button>
          {canSeeSalary && <button className={`ds-rp-tab${tab === 'paie' ? ' on' : ''}`} onClick={() => setTab('paie')}>💰 Paie</button>}
        </div>

        {tab === 'profile' && (
          <>
            {/* Salaire protégé (DAF only) */}
            {canSeeSalary && salaryFcfa != null && (
              <div className="ds-salary-protected">
                <div className="ds-salary-amount">{formatNumber(salaryFcfa)} FCFA</div>
                <div className="ds-salary-label">Salaire mensuel brut</div>
                <div className="ds-salary-lock"><Lock size={10} /> Visible DAF + admin uniquement</div>
              </div>
            )}
            {!canSeeSalary && employee.salaryCents && (
              <div className="ds-salary-protected" style={{ opacity: 0.6 }}>
                <div className="ds-salary-amount" style={{ fontSize: 14, color: 'var(--tx-3)' }}>● ● ● ● ● ●</div>
                <div className="ds-salary-label">Salaire masqué</div>
                <div className="ds-salary-lock"><Lock size={10} /> Réservé DAF + admin</div>
              </div>
            )}

            <div className="ds-dp-section">
              <div className="ds-dp-section-title">Identité & contact</div>
              <div className="ds-dp-grid">
                <div className="ds-dp-item"><label>Matricule</label><span style={{ fontFamily: 'var(--font-m)' }}>{employee.matricule}</span></div>
                <div className="ds-dp-item"><label>Département</label><span>{employee.department}</span></div>
                {employee.email && <div className="ds-dp-item full"><label>Email</label><span>{employee.email}</span></div>}
                {employee.phone && <div className="ds-dp-item full"><label>Téléphone</label><span>{employee.phone}</span></div>}
                {employee.dateNaissance && <div className="ds-dp-item full"><label>Naissance</label><span>{fmtDateLong(employee.dateNaissance)}{employee.lieuNaissance ? ` à ${employee.lieuNaissance}` : ''}</span></div>}
                {employee.numeroCnps && <div className="ds-dp-item"><label>N° CNPS</label><span>{employee.numeroCnps}</span></div>}
              </div>
            </div>

            <div className="ds-dp-section">
              <div className="ds-dp-section-title">Contrat</div>
              <div className="ds-dp-grid">
                <div className="ds-dp-item"><label>Type</label><span>{CONTRACT_TYPE_LABELS[employee.contractType]}</span></div>
                <div className="ds-dp-item"><label>Statut</label><span>{EMPLOYEE_STATUS_LABELS[employee.status]}</span></div>
                <div className="ds-dp-item"><label>Embauche</label><span>{fmtDateLong(employee.hireDate)}</span></div>
                <div className="ds-dp-item"><label>Ancienneté</label><span>{yearsSince(employee.hireDate)}</span></div>
                {employee.endDate && <div className="ds-dp-item full"><label>Fin de contrat</label><span style={{ color: 'var(--warning)' }}>{fmtDateLong(employee.endDate)}</span></div>}
              </div>
            </div>
          </>
        )}

        {tab === 'leaves' && (
          // TICKET-2026-004 (31/10/2026) : l'onglet affichait un solde écrit en dur (24 acquis, 6 pris, 18 restants),
          // identique pour tous les salariés. Un solde affiché par l'employeur engage : aucun chiffre tant que les
          // congés ne sont pas réellement calculés.
          <div className="ds-dp-section">
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--tx-3)', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: 'var(--tx-2)', marginBottom: 4 }}>Module Congés en préparation</div>
              Les droits acquis, les jours pris et le solde ne sont pas encore calculés par l'application.
              Aucun solde n'est donc affiché ici : seul le registre tenu par les RH fait foi.
            </div>
          </div>
        )}

        {tab === 'docs' && <DocumentsEmploye employee={employee} />}
        {tab === 'paie' && canSeeSalary && <PaieEmploye employee={employee} />}
      </div>

      {onEdit && (
        <div className="ds-dp-actions">
          <button className="ds-btn ds-btn-p" style={{ justifyContent: 'center' }} onClick={() => onEdit(employee)}>
            <Edit size={14} /> Modifier la fiche
          </button>
        </div>
      )}
    </div>
  );
};

